import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { CopilotKit } from "@copilotkit/react-core";
import "@copilotkit/react-ui/styles.css";

// Use direct routing components
import { MemoryRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "./views/Home";
import BottomNavigation from "./components/layouts/BottomNavigation";

// Import CSS
import "../../assets/css/index.css";

// Declare global Office object
declare const Office: any;

// Import store hooks and adapter
import {
  useAuthState,
  useAuthActions,
  useIsAuthenticated,
  useCurrentUser,
  useAuthError,
  useIsLoading,
} from "./store/authStore";
import { msalAdapter } from "./store/msalAdapter";

import { useOutlookAppointmentEvents } from "./hooks/useOutlookAppointmentEvents";
import useOfficeTheme from "./hooks/useOfficeTheme";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import queryClient from "../../libs/query";
import Toast from "./components/ui/Toast";
import Agent from "./views/Agent";
import { useOfficeEvents } from "./hooks/useOfficeEvents";
import { initializeStorageCleanup } from "./utils/storageUtils";

// AnimatedRoutes component with transition effects
const AnimatedRoutes = () => {
  return (
    <div className="tab-transition">
      <Toast />
      <Routes>
        <Route path="/" element={<Agent />} />
      </Routes>
    </div>
  );
};

const Taskpane: React.FC = () => {
  // Use store hooks instead of local state
  const authState = useAuthState();
  const isAuthenticated = useIsAuthenticated();
  const currentUser = useCurrentUser();
  const authError = useAuthError();
  const isLoading = useIsLoading();

  // Sync Tailwind dark mode with Outlook theme
  useOfficeTheme();

  // ✅ Initialize outlook events and sync
  useOutlookAppointmentEvents(); // Handles Outlook data → outlookStore
  useOfficeEvents(); // Handles outlookStore → other stores sync

  // Initialize authentication using MSAL adapter
  const initializeAuthentication = async () => {
    try {

      // Get login hint from Office context
      const accountType = Office.context?.mailbox?.userProfile?.accountType;
      const loginHint =
        accountType === "office365" || accountType === "outlookCom"
          ? Office.context.mailbox.userProfile.emailAddress
          : "";

      // Initialize MSAL adapter (which will update the store)
      await msalAdapter.initialize(loginHint);
    } catch (error) {
      console.error("Authentication initialization failed:", error);
      // Error handling is done by the MSAL adapter, which updates the store
    }
  };

  // Initialize storage cleanup and authentication when component mounts
  useEffect(() => {
    // Initialize storage cleanup first
    initializeStorageCleanup();
    
    // Then initialize authentication
    initializeAuthentication();
  }, []);

  // Render loading, error, or the main application based on authentication state
  const renderAuthStateContent = () => {
    // Check if still loading/initializing
    if (isLoading || !authState.isInitialized) {
      return (
        <div className="flex justify-center items-center h-full p-4 dark:bg-darkBackground">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            {/* <p className="text-gray-600">Initializing application...</p> */}
          </div>
        </div>
      );
    }

    // Check for authentication errors
    if (authError) {
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-4">
          <p className="font-bold">Authentication Error</p>
          <p className="text-sm">{authError}</p>
          <button
            onClick={initializeAuthentication}
            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm mt-2"
          >
            Retry
          </button>
        </div>
      );
    }

    // Check if authenticated successfully
    if (isAuthenticated && currentUser) {
      return (
        <>
          <div className="flex-1 overflow-auto content-with-bottom-nav">
            <AnimatedRoutes />
          </div>
          {/* <BottomNavigation /> */}
        </>
      );
    }

    // Fallback - still initializing
    return (
      <div className="flex justify-center items-center h-full p-4 dark:bg-darkBackground">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          {/* <p className="text-gray-600">Setting up authentication...</p> */}
        </div>
      </div>
    );
  };

  return (
    <MemoryRouter initialEntries={["/"]} initialIndex={0}>
      <div className="flex flex-col h-screen">{renderAuthStateContent()}</div>
    </MemoryRouter>
  );
};

// Initialize the component when Office is ready
Office.onReady((info: any) => {
  if (info.host === Office.HostType.Outlook) {
    const root = document.getElementById("taskpane-root");
    if (root) {
      ReactDOM.createRoot(root).render(
        <React.StrictMode>
          <QueryClientProvider client={queryClient}>
            <Taskpane />
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          </QueryClientProvider>
        </React.StrictMode>
      );
    }
  }
});

export default Taskpane;
